/**
 * Main application component for Kalpa Niyama.
 * 
 * Handles routing, authentication, and theme setup.
 * For documentation, see:
 * - docs/technical/FRONTEND_ARCHITECTURE.md
 * - docs/reference/USER_GUIDE.md
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { AnalyticsReferenceDateProvider } from './contexts/AnalyticsReferenceDateContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import Navbar from './components/Navbar';
import { useLayoutTokens } from './hooks/useLayoutTokens';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Habits from './pages/Habits';
import HabitReview from './pages/HabitReview';
import HabitStats from './pages/HabitStats';
import CheckIn from './pages/CheckIn';
import Commitments from './pages/Commitments';
import Timelines from './pages/Timelines';
import Timers from './pages/Timers';
import Reflections from './pages/Reflections';
import Projects from './pages/Projects';
import Planner from './pages/Planner';
import Calendar from './pages/Calendar';
import WorkTimers from './pages/WorkTimers';
import PlannedEvents from './pages/PlannedEvents';
import TimeEntries from './pages/TimeEntries';
import Reports from './pages/Reports';
import Integrations from './pages/Integrations';
import Exports from './pages/Exports';
import Activity from './pages/Activity';
import Help from './pages/Help';
import Profile from './pages/Profile';

function App() {
  const { user, loading } = useAuth();
  const { appMainSx } = useLayoutTokens();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <PreferencesProvider>
      <AnalyticsReferenceDateProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Navbar />
        <Box component="main" sx={appMainSx}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/habits" element={<Habits />} />
            <Route path="/habits/review" element={<HabitReview />} />
            <Route path="/habits/stats/:id" element={<HabitStats />} />
            <Route path="/check-in" element={<CheckIn />} />
            <Route path="/commitments" element={<Commitments />} />
            <Route path="/timelines" element={<Timelines />} />
            <Route path="/timers" element={<Timers />} />
            {/* "Countdowns" is the display name for the /timers section; keep
                /timers canonical and redirect the friendlier URLs to it. The
                older "Life Events" aliases still resolve for saved bookmarks. */}
            <Route path="/life-events" element={<Navigate to="/timers" replace />} />
            <Route path="/lifeEvents" element={<Navigate to="/timers" replace />} />
            <Route path="/reflections" element={<Reflections />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/planner" element={<Planner />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/work-timers" element={<WorkTimers />} />
            <Route path="/planned-events" element={<PlannedEvents />} />
            <Route path="/time-entries" element={<TimeEntries />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/exports" element={<Exports />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/help" element={<Help />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
      </AnalyticsReferenceDateProvider>
    </PreferencesProvider>
  );
}

export default App;
